import { Spin, message } from 'antd'
import { Trans, useLingui } from '@lingui/react/macro'

import { getLeafNodes, useLabelsTree } from './_hooks/useLabelsTree'
import StyleModal from './StyleModal'

import type { PresetLabelTreeNode } from '@loc/server/interface/preset'

import EnhanceTextArea from '@/components/EnhanceTextArea'
import ScrollArea from '@/pages/tad.ai/_components/ScrollArea'


import { useState, useEffect, useMemo } from 'react';
import { cls } from 'tagged-classnames-free';
import { CssIcon } from '@/components/CssIcon';
export interface StyleSelectorProps {
  value?: string
  onChange?: (value: string) => void
  maxLength?: number
  placeholder?: string
  hideViewMore?: boolean
}

/** 从文本值中解析已选标签列表 */
function parseSelectedTags(value: string): string[] {
  if (!value) return []
  return value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
}

/** 将标签追加到文本值中 */
function appendTag(currentValue: string, tag: string): string {
  const tags = parseSelectedTags(currentValue)
  if (tags.includes(tag)) {
    // 取消选择
    return tags.filter((t) => t !== tag).join(', ')
  }
  // 追加
  return currentValue ? `${currentValue}, ${tag}` : tag
}

function StyleSelector(props: StyleSelectorProps) {
  const {
    value = '',
    onChange,
    maxLength = 300,
    placeholder,
    hideViewMore,
  } = props
  const { t } = useLingui()

  const { tree, isLoading } = useLabelsTree()
  const [modalOpen, setModalOpen] = useState(false)
  const [activeCategory, setActiveCategory] = useState<string>('')

  const selectedTags = parseSelectedTags(value)

  // 默认选中第一个一级分类
  useEffect(() => {
    if (tree.length > 0 && !activeCategory) {
      setActiveCategory(tree[0].code)
    }
  }, [tree, activeCategory])

  // 当前一级分类下的前 N 个三级 leaf 标签
  const categoryTags = useMemo(() => {
    if (tree.length === 0) return []
    const activeNode = tree.find((n) => n.code === activeCategory)
    if (!activeNode) return []
    return getLeafNodes([activeNode])
  }, [tree, activeCategory])

  const handleTagToggle = (tag: PresetLabelTreeNode) => {
    const newValue = appendTag(value, tag.displayName)
    onChange?.(newValue)
  }
  const showLoadingState = isLoading && tree.length === 0

  // 底部内容：count + clear + 分隔线 + 标签区域
  const tagsBottomChildren = (
    <>
      {/* count + clear 行 */}
      <div className='flex justify-between gap-2'>
        <div className='scrollbar flex flex-1 items-center overflow-x-auto pl-[11px]'>
          <div className={`text-f-text-quaternary py-2 text-xs`}>
            {value.length}/{maxLength} <Trans>characters</Trans>
          </div>
        </div>
        <div className='flex items-center gap-3 pb-[2px] pt-2'>
          <div
            className={cls`text-f-text-quaternary hover:text-f-text-secondary cursor-pointer transition-colors`}
            onClick={() => {
              const fakeEvent = {
                target: { value: '' },
              } as React.ChangeEvent<HTMLTextAreaElement>
              onChange?.(fakeEvent.target.value)
              message.success(t`Cleared successfully!`)
            }}
          >
            <span className='i-com--trash size-4'></span>
          </div>
        </div>
      </div>

      {/* 一级分类 Tab 行 + View More */}
      {showLoadingState && (
        <div
          className={cls`border-f-border-secondary ml-[11px] mr-0 border-t pt-3`}
        >
          <div className={cls`flex h-[110px] items-center justify-center`}>
            <div
              className={cls`text-f-text-quaternary flex items-center gap-2 text-sm`}
            >
              <Spin size='small' />
              <Trans>Loading styles...</Trans>
            </div>
          </div>
        </div>
      )}

      {!showLoadingState && tree.length > 0 && (
        <div
          className={cls`border-f-border-secondary ml-[11px] mr-0 border-t pt-2`}
        >
          <div className={cls`flex items-center gap-3`}>
            <div className={cls`relative min-w-0 flex-1`}>
              <ScrollArea
                orientation='horizontal'
                scrollbarVisibility='hoverOrScroll'
                className={cls`overflow-hidden pb-[6px]`}
              >
                <div className={cls`flex items-center gap-5`}>
                  {tree.map((category) => {
                    const isActive = activeCategory === category.code
                    return (
                      <button
                        key={category.code}
                        type='button'
                        className={cls`
                          shrink-0 cursor-pointer bg-transparent text-sm transition-all
                          ${isActive ? 'text-f-text font-semibold' : 'text-f-text-tertiary hover:text-f-text'}
                        `}
                        onClick={() => setActiveCategory(category.code)}
                      >
                        {category.displayName}
                      </button>
                    )
                  })}
                </div>
              </ScrollArea>
            </div>
            {!hideViewMore && (
              <button
                type='button'
                className={cls`
                  text-f-text-quaternary hover:text-f-text shrink-0 cursor-pointer pb-[6px] text-sm
                `}
                onClick={() => setModalOpen(true)}
              >
                <Trans>View More</Trans>
              </button>
            )}
          </div>

          {/* 三级标签流式布局 */}
          <div className={cls`pb-4`}>
            <ScrollArea
              scrollbarVisibility='always'
              className={cls`max-h-[calc(68px+12px)] overflow-hidden`}
            >
              <div className={cls`flex flex-wrap gap-2 pr-[2px] pt-[6px]`}>
                {categoryTags.map((tag) => {
                  const isSelected = selectedTags.includes(tag.displayName)
                  return (
                    <button
                      key={tag.code}
                      type='button'
                      className={cls`
                        group/tag relative flex h-[30px] cursor-pointer items-center rounded px-3 text-sm transition-colors
                        ${isSelected ? 'bg-f-primary-style-1 text-f-primary' : ' text-f-text-tertiary hover:text-f bg-[#252533] hover:bg-[#383850]'}
                      `}
                      onClick={() => handleTagToggle(tag)}
                    >
                      {tag.displayName}
                      {/* 选中态减号 badge */}
                      {isSelected && (
                        <CssIcon
                          className={cls`
                            bg-f-primary i-com--circle-gap-part-solid absolute -right-1.5 -top-1.5 size-3
                          `}
                        >
                          -
                        </CssIcon>
                      )}
                    </button>
                  )
                })}
              </div>
            </ScrollArea>
          </div>
        </div>
      )}
    </>
  )

  return (
    <>
      <EnhanceTextArea
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder={placeholder || t`Describe your style…`}
        size='large'
        count={{ max: maxLength, show: false }}
        rows={6}
        style={{ resize: 'none' }}
        className={cls`text-sm`}
        containerClassName={cls`
          border-f-border-secondary
          bg-f-bg-elevated
          hover:bg-f-bg-elevated
        `}
        bottomChildren={tagsBottomChildren}
      />

      {/* View More 弹窗 */}
      <StyleModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        tree={tree}
        isLoading={isLoading}
        selectedTags={selectedTags}
        onTagToggle={handleTagToggle}
      />
    </>
  )
}

export default StyleSelector
